import React from 'react';
import PropTypes from 'prop-types';
import { Stack } from '@mui/material';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import { useRecoilState } from 'recoil';
import { CssTextField } from 'components/select';
import Btn from 'components/button';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { orderPayload, certCreationRequired, customDomainFieldsDiasbled, isValidCostCenter } from 'pages/provisioning/virtual-service/store';
import InfoImg from 'components/info-img';
import { COST_CENTER_H_TOOLTIP_TEXT, COST_CENTER_H_PREFIX_HELPER_TEXT, COST_CENTER_VALIDATE_HELPER_TEXT } from 'utils/constant';

export const CostCenter = ({ onBudgetCodeValidationFailure }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(orderPayload);
    const [isRequired] = useRecoilState(certCreationRequired);
    const [isDisabled] = useRecoilState(customDomainFieldsDiasbled);
    const [validCostCenter, setValidCostCenter] = useRecoilState(isValidCostCenter);
    const [apiValidationMessage, setApiValidationMessage] = React.useState('');

    // Cost Center stays disabled until a custom domain (requiring certificate creation) is configured
    const fieldDisabled = !isRequired || isDisabled;
    const hPrefixError = /^[Hh]/.test(order?.costCenter || '');

    const handleBudgetCodeResult = (isSuccess, errorMessage) => {
        if (!isSuccess) {
            setValidCostCenter(false);
            if (onBudgetCodeValidationFailure) {
                onBudgetCodeValidationFailure({ budgetCode: order?.costCenter?.trim(), errorMessage });
            } else {
                setApiValidationMessage(errorMessage);
            }
        } else {
            setApiValidationMessage('');
            setValidCostCenter(true);
        }
    };

    const validateCostCenterApi = useMutation((payload) => axios.post(endPoints.ssp.vm.budgetCode, payload), {
        onSuccess: (data) => {
            const isValid = Boolean(data?.data?.valid);
            handleBudgetCodeResult(isValid, isValid ? '' : 'Budget Code is invalid');
        },
        onError: (error) => {
            const msg = (Array.isArray(error) ? error[0] : undefined) || 'Budget Code is not valid';
            handleBudgetCodeResult(false, msg);
        },
    });

    const handleChange = (e) => {
        const val = e.target.value;
        setApiValidationMessage('');
        setValidCostCenter(false);
        setOrder({
            ...order,
            costCenter: val || null,
            costCenterDetails: val ? { label: val, value: val } : null,
        });
    };

    const handleValidate = () => {
        const trimmedVal = order?.costCenter?.trim();
        if (trimmedVal && !hPrefixError) {
            validateCostCenterApi.mutate({ budgetCode: trimmedVal });
        }
    };

    React.useEffect(() => {
        if (!order?.costCenter && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.costCenter, order?.isTouched]);

    const apiValidationError = Boolean(apiValidationMessage);
    const isNotValidated = Boolean(order?.costCenter && !validCostCenter && order?.enabledManualBudgetCode !== true);
    const isInvalid = (isTouched && order?.isTouched && !order?.costCenter) || hPrefixError || apiValidationError || isNotValidated;
    const getValidate = () => {
        let validateText = '';
        if (isRequired) {
            if (isInvalid) {
                validateText = 'validate';
            } else if (!order?.costCenter) {
                validateText = 'validate1';
            }
        }
        return validateText;
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center" data-testid="cost-center-box">
            <CssTextField
                key="costCenterName"
                size="small"
                label="Budget Code (H-code, Cross Charge) *"
                fullWidth
                autoComplete="off"
                value={order?.costCenter || ''}
                onChange={handleChange}
                disabled={fieldDisabled}
                error={hPrefixError || apiValidationError || isNotValidated}
                helperText={hPrefixError ? COST_CENTER_H_PREFIX_HELPER_TEXT : apiValidationMessage || (isNotValidated ? COST_CENTER_VALIDATE_HELPER_TEXT : '')}
                // sx={{ width: '100%' }}
                className={getValidate()}
            />
            <Btn
                className="btn-disabled custom-btn"
                onClick={handleValidate}
                disabled={!order?.costCenter || hPrefixError || validateCostCenterApi.isLoading}
                size="small"
                sx={ProvisioningStyles.dataDisk.configureBtn}
                variant="outlined"
                data-testid="validate-btn"
            >
                Validate*
            </Btn>
            <InfoImg
                title={
                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                        <li>Budget code is required only if there is a need for certificate creation for given custom domain.</li>
                        <li>{COST_CENTER_H_TOOLTIP_TEXT}</li>
                    </ul>
                }
            />
        </Stack>
    );
};

CostCenter.propTypes = { onBudgetCodeValidationFailure: PropTypes.func };
CostCenter.defaultProps = { onBudgetCodeValidationFailure: undefined };
export default React.memo(CostCenter);
